import { pgTable, text, serial, integer, boolean, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// User schema (keeping as reference)
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
});

export const insertUserSchema = z.object({
  username: z.string(),
  password: z.string(),
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// Services schema
export const services = pgTable("services", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  category: text("category").notNull(),
  description: text("description").notNull(),
  imageUrl: text("image_url"),
  price_from: integer("price_from"),
  price_to: integer("price_to"),
  turnaround_time: text("turnaround_time"),
});

export const insertServiceSchema = z.object({
  name: z.string(),
  slug: z.string(),
  category: z.string(),
  description: z.string(),
  imageUrl: z.string().optional(),
  price_from: z.number().optional(),
  price_to: z.number().optional(),
  turnaround_time: z.string().optional()
});

export type InsertService = z.infer<typeof insertServiceSchema>;
export type Service = typeof services.$inferSelect;

// Testimonials schema
export const testimonials = pgTable("testimonials", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  service: text("service").notNull(),
  rating: integer("rating").notNull(),
  content: text("content").notNull(),
  initials: text("initials").notNull(),
});

export const insertTestimonialSchema = z.object({
  name: z.string(),
  service: z.string(),
  rating: z.number(),
  content: z.string(),
  initials: z.string()
});

export type InsertTestimonial = z.infer<typeof insertTestimonialSchema>;
export type Testimonial = typeof testimonials.$inferSelect;

// Locations schema
export const storeLocations = pgTable("store_locations", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  address: text("address").notNull(),
  phone: text("phone").notNull(),
  hours: text("hours").notNull(),
});

export const insertLocationSchema = z.object({
  name: z.string(),
  address: z.string(),
  phone: z.string(),
  hours: z.string()
});

export type InsertLocation = z.infer<typeof insertLocationSchema>;
export type Location = typeof storeLocations.$inferSelect;

// FAQ schema
export const faqs = pgTable("faqs", {
  id: serial("id").primaryKey(),
  question: text("question").notNull(),
  answer: text("answer").notNull(),
});

export const insertFaqSchema = z.object({
  question: z.string(),
  answer: z.string()
});

export type InsertFaq = z.infer<typeof insertFaqSchema>;
export type Faq = typeof faqs.$inferSelect;

// Appointment schema
export const appointments = pgTable("appointments", {
  id: serial("id").primaryKey(),
  fullName: text("full_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  location: text("location").notNull(),
  deviceType: text("device_type").notNull(),
  deviceBrand: text("device_brand").notNull(),
  deviceModel: text("device_model").notNull(),
  serviceNeeded: text("service_needed").notNull(),
  additionalInfo: text("additional_info"),
  preferredDate: text("preferred_date").notNull(),
  preferredTime: text("preferred_time").notNull(),
  status: text("status").default("pending").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  terms: boolean("terms").default(false).notNull(),
});

export const insertAppointmentSchema = z.object({
  fullName: z.string(),
  email: z.string().email(),
  phone: z.string(),
  location: z.string(),
  deviceType: z.string(),
  deviceBrand: z.string(),
  deviceModel: z.string(),
  serviceNeeded: z.string(),
  preferredDate: z.string(),
  preferredTime: z.string(),
  additionalInfo: z.string().optional(),
  terms: z.boolean().refine(val => val === true, { message: 'Terms must be accepted' })
});

export type InsertAppointment = z.infer<typeof insertAppointmentSchema>;
export type Appointment = typeof appointments.$inferSelect;
